import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type {
  Agent,
  Conversation,
  CreateAgentInput,
  CreateTenantInput,
  Member,
  Page,
  Role,
  ScopeFilter,
  Tenant,
  UpdateAgentInput,
  UpdateTenantInput,
} from './types';

/** Trae todas las páginas de un listado paginado por cursor (tope defensivo: 10 páginas de 100). */
export async function fetchAll<T>(path: string, query: Record<string, string | number | undefined> = {}): Promise<T[]> {
  const out: T[] = [];
  let cursor: string | undefined;
  for (let i = 0; i < 10; i++) {
    const page = await api.get<Page<T>>(path, { ...query, limit: 100, cursor });
    out.push(...page.items);
    if (!page.nextCursor) break;
    cursor = page.nextCursor;
  }
  return out;
}

export const tenantsQuery = queryOptions({
  queryKey: ['tenants'],
  queryFn: () => fetchAll<Tenant>('/admin/tenants'),
});

export const tenantQuery = (tenantId: string) =>
  queryOptions({ queryKey: ['tenants', tenantId], queryFn: () => api.get<Tenant>(`/admin/tenants/${tenantId}`) });

export const agentsQuery = (tenantId: string) =>
  queryOptions({
    queryKey: ['tenants', tenantId, 'agents'],
    queryFn: () => fetchAll<Agent>(`/admin/tenants/${tenantId}/agents`),
  });

export const agentQuery = (tenantId: string, agentId: string) =>
  queryOptions({
    queryKey: ['tenants', tenantId, 'agents', agentId],
    queryFn: () => api.get<Agent>(`/admin/tenants/${tenantId}/agents/${agentId}`),
  });

export const membersQuery = (tenantId: string) =>
  queryOptions({
    queryKey: ['tenants', tenantId, 'members'],
    queryFn: async () => await api.get<{ items: Member[] } | Member[]>(`/admin/tenants/${tenantId}/members`),
    select: (d): Member[] => (Array.isArray(d) ? d : d.items),
  });

export const conversationsQuery = (tenantId: string, limit = 10) =>
  queryOptions({
    queryKey: ['tenants', tenantId, 'conversations', limit],
    queryFn: () => api.get<Page<Conversation>>(`/admin/tenants/${tenantId}/conversations`, { limit }),
  });

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTenantInput) => api.post<Tenant>('/admin/tenants', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenants'], exact: true });
      void qc.invalidateQueries({ queryKey: ['session'] });
      void qc.invalidateQueries({ queryKey: ['demos'] });
    },
  });
}

export function useUpdateTenant(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTenantInput) => api.patch<Tenant>(`/admin/tenants/${tenantId}`, input),
    onSuccess: (t) => {
      qc.setQueryData(['tenants', tenantId], t);
      void qc.invalidateQueries({ queryKey: ['tenants'], exact: true });
      void qc.invalidateQueries({ queryKey: ['demos'] });
    },
  });
}

export function useCreateAgent(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAgentInput) => api.post<Agent>(`/admin/tenants/${tenantId}/agents`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenants', tenantId, 'agents'] });
      void qc.invalidateQueries({ queryKey: ['demos'] });
    },
  });
}

export function useUpdateAgent(tenantId: string, agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAgentInput) => api.patch<Agent>(`/admin/tenants/${tenantId}/agents/${agentId}`, input),
    onSuccess: (a) => {
      qc.setQueryData(['tenants', tenantId, 'agents', agentId], a);
      void qc.invalidateQueries({ queryKey: ['tenants', tenantId, 'agents'], exact: true });
      void qc.invalidateQueries({ queryKey: ['demos'] });
    },
  });
}

export function useDeleteAgent(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) =>
      api.del<{ result: 'deleted' | 'archived' }>(`/admin/tenants/${tenantId}/agents/${agentId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenants', tenantId, 'agents'] });
      void qc.invalidateQueries({ queryKey: ['demos'] });
    },
  });
}

export function useUpdateMemberRole(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; role: Role }) =>
      api.patch<unknown>(`/admin/tenants/${tenantId}/members/${input.userId}`, { role: input.role }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tenants', tenantId, 'members'] }),
  });
}

export function useUpdateMemberScope(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; scopeFilter: ScopeFilter | null }) =>
      api.patch<unknown>(`/admin/tenants/${tenantId}/members/${input.userId}`, { scopeFilter: input.scopeFilter }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tenants', tenantId, 'members'] }),
  });
}

export function useRemoveMember(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.del<unknown>(`/admin/tenants/${tenantId}/members/${userId}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tenants', tenantId, 'members'] }),
  });
}
